import { Checksum, MemberRole } from "@jnctn/api/app/fetchChecksums";
import fetchLocations, { Location } from "@jnctn/api/app/fetchLocations";
import { Member, fetchMember } from "@jnctn/api/app/fetchMemberFilter";
import { useContext, useEffect } from "react";
import { useAsyncResource } from "use-async-resource";
import LocationContext, {
  DEFAULT,
} from "@jnctn/bridge/contexts/LocationContext";
import TenantContext from "@jnctn/bridge/contexts/TenantContext";
import useChecksumDefaults from "@jnctn/bridge/auth/useChecksumDefaults";
import { useAuthContext } from "../../contexts/AuthContextProvider";
import {
  IncompleteProfileError,
  TenantErrorInactive,
  TenantErrorNoActiveTenants,
  TenantErrorNoTenants,
} from "@jnctn/bridge/auth/authHooks";
import { useChecksums } from "./useChecksums";

export { useChecksums };

export function useUser() {
  const { user } = useAuthContext();
  return user;
}

export function useAccessToken(): string | undefined {
  const { accessToken } = useAuthContext();
  return accessToken;
}

export function useMember(): Member | undefined {
  return useMemberForChecksum(useTenantChecksum());
}

export function useMemberForChecksum(
  tenantChecksum: Checksum
): Member | undefined {
  const [reader] = useAsyncResource(
    fetchMember,
    useAccessToken(),
    tenantChecksum.tenantId,
    tenantChecksum.checksum
  );

  const memberResult = reader()?.pages;

  if (tenantChecksum === undefined) {
    return undefined;
  }

  const [member] = memberResult;

  return member;
}

export function useLocations(): Array<Location> {
  const accessToken = useAccessToken();
  const tenantChecksum = useTenantChecksum();

  const [reader] = useAsyncResource(
    fetchLocations,
    0,
    1000,
    accessToken,
    tenantChecksum?.tenantId as string
  );

  return reader().pages;
}

export function useLocation(): Location | undefined {
  const accessToken = useAccessToken();
  const tenantChecksum = useTenantChecksum();

  // eslint-disable-next-line prefer-const
  let { location, setLocation } = useContext(LocationContext);
  const [reader] = useAsyncResource(
    fetchLocations,
    0,
    1,
    accessToken,
    tenantChecksum?.tenantId as string,
    undefined,
    location ? [location] : undefined
  );

  const fetchedLocation =
    reader().pages.length !== 0 ? reader().pages[0] : undefined;

  if (
    location !== DEFAULT &&
    (!fetchedLocation || fetchedLocation.did !== location)
  ) {
    location = DEFAULT;
  }

  useEffect(() => {
    if (location) {
      setLocation(location);
    }
  }, [location, setLocation]);

  return fetchedLocation && fetchedLocation.did === location
    ? fetchedLocation
    : undefined;
}

export function useTenantChecksum(): Checksum | undefined {
  const { accessToken } = useAuthContext();

  // eslint-disable-next-line prefer-const
  let { tenant, setTenant } = useContext(TenantContext);

  const checksums = useChecksums();

  if (accessToken && checksums.length === 0) {
    throw new TenantErrorNoTenants();
  }

  // First check that tenant in context is valid
  let selectedChecksum = checksums.find(
    ({ tenantName }) => tenantName === tenant
  );

  const completeProfileChecksums = checksums.filter(
    ({ profileComplete }) =>
      profileComplete === true || profileComplete === undefined // remove the undefined condition after the API is updated
  );

  const activeChecksums = checksums.filter(({ isActive }) => isActive);

  const validChecksums = completeProfileChecksums.filter(
    ({ isActive }) => isActive
  );

  if (accessToken) {
    if (selectedChecksum) {
      if (
        !completeProfileChecksums.find(
          (checksum) => checksum.tenantName === tenant
        )
      ) {
        throw new IncompleteProfileError(undefined, "incomplete profile error");
      }
      if (!activeChecksums.find((checksum) => checksum.tenantName === tenant)) {
        throw new TenantErrorInactive(selectedChecksum);
      }
    } else {
      if (completeProfileChecksums.length === 0) {
        if (activeChecksums.length === 0) {
          throw new TenantErrorNoActiveTenants();
        } else {
          throw new IncompleteProfileError(
            activeChecksums[0].tenantName,
            "incomplete profile error"
          );
        }
      } else {
        if (activeChecksums.length === 0) {
          throw new TenantErrorInactive(completeProfileChecksums[0]);
        } else {
          if (validChecksums.length === 0) {
            throw new IncompleteProfileError(
              activeChecksums[0].tenantName,
              "incomplete profile error"
            );
          }
        }
      }
    }
  }

  const { defaultTenant, defaultSelectedChecksum } = useChecksumDefaults(
    accessToken,
    selectedChecksum,
    tenant,
    validChecksums
  );

  tenant = defaultTenant;
  selectedChecksum = defaultSelectedChecksum;

  // Update the tenant if we have prefilled one
  useEffect(() => {
    if (tenant) {
      setTenant(tenant);
    }
  }, [tenant, setTenant]);

  // Return the actual checksum
  return selectedChecksum!;
}

const TRAINER_ROLES = new Set<MemberRole | undefined>([
  "Trainer / Assessor",
  "Organisation Admin",
  "Admin",
  "Coreadmin",
]);

export function useIsTrainer(): boolean {
  return TRAINER_ROLES.has(useTenantChecksum()?.memberRole);
}

const VERIFIER_ROLES = new Set<MemberRole | undefined>([
  "Verifier",
  "Organisation Admin",
  "Admin",
  "Coreadmin",
]);

export function useIsVerifier(): boolean {
  return VERIFIER_ROLES.has(useTenantChecksum()?.memberRole);
}
